import { useRouteLoaderData } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import type { loader as rootLoader } from '@/root';
import type { Buddy } from '@/store/user';
import { siteSettingsFallback } from '@/cms/site-settings';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { useUserStore } from '@/store/user';

/**
 * Whether this is the kids path, where the Kangaroo Beach characters appear:
 * a lifeguard coaches, and the character the learner chose stands on their side.
 *
 * The characters are a primary-school device, and a seventeen-year-old or an
 * adult being coached by a cartoon kangaroo undercuts the material (ticket 23
 * §2). `audience === 'child'` is the whole condition, and it is read here
 * rather than threaded through `ChatRenderContext` so the thread and the
 * journey's greeting panel decide it the same way from the same source.
 */
export function useShowsBuddy(): boolean {
  return useUserStore((state) => state.profile?.audience) === 'child';
}

/** The kids coach from Site Settings, or its bundled fallback outside the root loader. */
function useKidsCoach() {
  return useRouteLoaderData<typeof rootLoader>('root')?.siteSettings.kidsCoach ?? siteSettingsFallback.kidsCoach;
}

/**
 * The coach's display name: the kids coach's own name (Site Settings, e.g.
 * Sandy) on the kids path, and the translated "Surf Lifesaver" elsewhere.
 */
export function useCoachName(): string {
  const { chat } = useStrings();
  const kidsCoach = useKidsCoach();

  return useShowsBuddy() ? kidsCoach.name : chat.chat_coach_name;
}

/**
 * The coach's face in the thread: the kids coach on the kids path, and the
 * surf lifesaver on every other one. One per bubble group rather than one per
 * message — grouping is what makes a long thread read as a conversation
 * instead of a list.
 */
export function CoachAvatar({ className }: { className?: string }) {
  const showsBuddy = useShowsBuddy();
  const kidsCoach = useKidsCoach();

  if (!showsBuddy) {
    return <CoachMark className={cn('size-9', className)} />;
  }

  return <BadgeAvatar src={kidsCoach.avatarUrl} alt={kidsCoach.name} className={cn('size-9', className)} />;
}

/** The learner's chosen character, on the kids path's user messages and the modules greeting. */
export function BuddyAvatar({ buddy, className }: { buddy: Buddy; className?: string }) {
  return <BadgeAvatar src={`/images/avatar-${buddy}.webp`} alt="" className={cn('size-9', className)} />;
}

/**
 * A character bursting out of its round badge, the way the Kangaroo Beach
 * artwork is drawn. The box is the badge's size; the picture, badge at its
 * foot, is taller than the box and rises out of the top, so the head breaks
 * the circle. Nothing clips it, so the box must sit where there is room above.
 */
function BadgeAvatar({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return (
    <span className={cn('relative block shrink-0', className)}>
      <img
        src={src}
        alt={alt}
        draggable={false}
        className="pointer-events-none absolute bottom-0 left-1/2 h-[175%] w-auto max-w-none -translate-x-1/2 drop-shadow-[0_1px_2px_rgb(16_24_64/0.18)]"
      />
    </span>
  );
}

/**
 * The coach for teens and adults: `coach_avatar` from Site Settings, or the
 * bundled Surf Life Saving roundel, clipped to a circle.
 *
 * Uses `useRouteLoaderData` rather than `useRootLoaderData` so it still
 * renders in an error boundary, where the root loader never ran.
 */
export function CoachMark({ className }: { className?: string }) {
  const { chat } = useStrings();
  const avatarUrl =
    useRouteLoaderData<typeof rootLoader>('root')?.siteSettings.coachAvatarUrl ?? siteSettingsFallback.coachAvatarUrl;

  return (
    <img
      src={avatarUrl}
      alt={chat.chat_coach_name}
      className={cn('shrink-0 rounded-full object-cover shadow-[0_1px_2px_rgb(16_24_64/0.12)]', className)}
    />
  );
}

/** Three dots in a coach bubble. Finite, and unmounted the moment the message lands. */
export function TypingDots() {
  const { chat } = useStrings();
  const coachName = useCoachName();

  return (
    <span
      role="status"
      aria-label={formatString(chat.chat_coach_is_typing, { name: coachName })}
      className="flex items-center gap-1 py-0.5"
    >
      <i className="chat-typing-dot bg-chat-ink-muted size-2 rounded-full" />
      <i className="chat-typing-dot bg-chat-ink-muted size-2 rounded-full" />
      <i className="chat-typing-dot bg-chat-ink-muted size-2 rounded-full" />
    </span>
  );
}
